import React, {FC, useEffect, useMemo} from 'react';
import {
    Flex,
    RadioButton,
    SelectSizes,
    SelectV2,
    Text,
    ToggleSwitch
} from '@priority-software/priority-style-react';
import {ToggleButton} from '../../../../../components/ToggleButton';
import {CheckedValue} from '../../../../../components/ToggleButton/enum';
import {OptimizationDialogState} from '../OptimizationDialog';
import styles from './OptimizationDialogContent.module.scss';
import {OptimizationDialogConsiderOption, OptimizationDialogOpenValue} from '../';
import {AutoPlanningContent} from './AutoPlanningContent';
import {ColumnsNamesForRoutes, REFDES} from '../../../constants';
import {Form} from '../../../../../stores/Form';
import {texts} from '../../../texts';
import {ModernTooltip} from '../../../../../components/Common/Tooltip/ModernTooltip';
import {RouteType} from '../../../types';
import {UtilizationValue} from '../UtilizationWarning';

export type OptimizationDialogContentProps = {
    optimizationDialogState: OptimizationDialogState;
    setOptimizationDialogState: React.Dispatch<React.SetStateAction<OptimizationDialogState>>;
    openValue: OptimizationDialogOpenValue;
    deliveryDateValue: string;
    setDeliveryDateValue: (value: string) => void;
    planningForm?: Form;
    selectedRoutes: RouteType[];
    zIndex: number;
    utilizationValue: UtilizationValue;
};

type OptionLabelProps = {
    label: string;
    tooltipText?: string;
};

const OptionLabel: FC<OptionLabelProps> = ({label, tooltipText}) => {
    const text = (
        <Text componentClasses={styles.label} skin={'paragraph-2'} textColor={'primary-700'}>
            {label}
        </Text>
    );

    return tooltipText ? (
        <ModernTooltip
            tooltip={
                <>
                    {tooltipText.split('\n').map((x, index) => (
                        <React.Fragment key={index}>
                            {x}
                            <br />
                        </React.Fragment>
                    ))}
                </>
            }
            maxWidth={400}
            arrowOffset={8}
            position="top-start"
            tooltipOffset={8}
        >
            {text}
        </ModernTooltip>
    ) : (
        text
    );
};

export const OptimizationDialogContent: FC<OptimizationDialogContentProps> = ({
    optimizationDialogState,
    setOptimizationDialogState,
    openValue,
    deliveryDateValue,
    setDeliveryDateValue,
    planningForm,
    selectedRoutes,
    zIndex,
    utilizationValue
}) => {
    const deliveryDateCol = planningForm?.getColumnByName(ColumnsNamesForRoutes.DISTRDATE);

    const isSelectDisabled =
        selectedRoutes.length !== 1 || !optimizationDialogState.runOnlyOnSelected;

    const selectFromOptions = useMemo(() => {
        const result = [];

        if (selectedRoutes.length === 1 && selectedRoutes[0].locations.length > 0) {
            const locationFormColumns = selectedRoutes[0].locations[0].data.parentForm.columns;
            const REFDES_id = locationFormColumns.find(x => x.Name === REFDES).ID;

            for (const location of selectedRoutes[0].locations) {
                result.push({
                    label: `${location.data.recordIndex + 1} ${
                        location.data.getField(REFDES_id as number).currentValue
                    }`,
                    value: location.id
                });
            }
        }

        return result;
    }, [selectedRoutes]);

    useEffect(() => {
        if (selectFromOptions.length > 0 && optimizationDialogState.runOnlyOnSelected) {
            if (optimizationDialogState.startLocationId !== selectFromOptions[0].value) {
                setOptimizationDialogState({
                    ...optimizationDialogState,
                    startLocationId: selectFromOptions[0].value
                });
            }
        } else {
            if (optimizationDialogState.startLocationId !== undefined) {
                setOptimizationDialogState({
                    ...optimizationDialogState,
                    startLocationId: undefined
                });
            }
        }
    }, [optimizationDialogState.runOnlyOnSelected, selectFromOptions]);

    return (
        <Flex direction={'column'} componentClasses={styles.wrapper}>
            {openValue === OptimizationDialogOpenValue.UnassignedOpen && (
                <Flex
                    direction={'row'}
                    justifyContent={'flex-start'}
                    componentClasses={styles.unassignedOptimizationOption}
                >
                    <ToggleSwitch
                        id={'unassignedOptimization'}
                        onChange={value =>
                            setOptimizationDialogState(optimizationDialogState => ({
                                ...optimizationDialogState,
                                unassignedOptimization: value
                            }))
                        }
                        checked={optimizationDialogState.unassignedOptimization}
                        disabled={utilizationValue === UtilizationValue.ExceedsNumber}
                    />
                    <OptionLabel
                        label={texts.unassigned_optimization_ai_title}
                        tooltipText={texts.unassigned_optimization_ai_placeholder}
                    />
                </Flex>
            )}
            {(
                openValue === OptimizationDialogOpenValue.UnassignedOpen
                    ? optimizationDialogState.unassignedOptimization
                    : true
            ) ? (
                <>
                    {openValue === OptimizationDialogOpenValue.UnassignedOpen && (
                        <AutoPlanningContent
                            deliveryDateCol={deliveryDateCol}
                            deliveryDateValue={deliveryDateValue}
                            setDeliveryDateValue={setDeliveryDateValue}
                            inputRequired
                        />
                    )}
                    {openValue === OptimizationDialogOpenValue.UnassignedOpen && (
                        <Flex direction={'column'} componentClasses={styles.option}>
                            <OptionLabel
                                label={texts.unassigned_consider_title}
                                tooltipText={texts.unassigned_consider_tooltip}
                            />
                            <RadioButton
                                wrapperClasses={styles.considerGroup}
                                isHorizontal
                                options={[
                                    {
                                        label: texts.unassigned_consider_route,
                                        value: OptimizationDialogConsiderOption.Route
                                    },
                                    {
                                        label: texts.unassigned_consider_area,
                                        value: OptimizationDialogConsiderOption.Area
                                    },
                                    {
                                        label: texts.unassigned_consider_none,
                                        value: OptimizationDialogConsiderOption.None
                                    }
                                ]}
                                selected={optimizationDialogState.consider}
                                onChange={value =>
                                    setOptimizationDialogState(optimizationDialogState => ({
                                        ...optimizationDialogState,
                                        consider: value
                                    }))
                                }
                            />
                        </Flex>
                    )}
                    <Flex direction={'column'} componentClasses={styles.option}>
                        <OptionLabel
                            label={texts.route_optimization_optimize_by}
                            tooltipText={texts.route_optimization_optimize_by_tooltip}
                        />
                        <ToggleButton
                            componentClasses={styles.toggleButton}
                            checked={
                                optimizationDialogState.optimizeByDistance
                                    ? CheckedValue.right
                                    : CheckedValue.left
                            }
                            onChange={value =>
                                setOptimizationDialogState(optimizationDialogState => ({
                                    ...optimizationDialogState,
                                    optimizeByDistance: value === CheckedValue.right
                                }))
                            }
                            left={texts.route_optimization_optimize_by_time}
                            right={texts.route_optimization_optimize_by_distance}
                            itemWidth={'fit-content'}
                        />
                    </Flex>
                    {openValue === OptimizationDialogOpenValue.UnassignedOpen && (
                        <Flex direction={'column'} componentClasses={styles.option}>
                            <OptionLabel
                                label={texts.unassigned_consideration_title}
                                tooltipText={texts.unassigned_consideration_tooltip}
                            />
                            <ToggleSwitch
                                id={'constraintsConsideration'}
                                onChange={value =>
                                    setOptimizationDialogState(optimizationDialogState => ({
                                        ...optimizationDialogState,
                                        constraintsConsideration: value
                                    }))
                                }
                                checked={optimizationDialogState.constraintsConsideration}
                            />
                        </Flex>
                    )}
                    <Flex direction={'column'} componentClasses={styles.option}>
                        <OptionLabel
                            label={texts.route_optimization_time_window_consideration}
                            tooltipText={texts.route_optimization_time_window_consideration_tooltip}
                        />
                        <ToggleSwitch
                            id={'timeWindowConsideration'}
                            onChange={value =>
                                setOptimizationDialogState(optimizationDialogState => ({
                                    ...optimizationDialogState,
                                    timeWindowConsideration: value
                                }))
                            }
                            checked={optimizationDialogState.timeWindowConsideration}
                        />
                    </Flex>
                    {openValue === OptimizationDialogOpenValue.Open && (
                        <Flex direction={'column'} componentClasses={styles.option}>
                            <OptionLabel
                                label={texts.route_optimization_start_from}
                                tooltipText={texts.route_optimization_start_from_tooltip}
                            />
                            <SelectV2
                                isDisabled={isSelectDisabled}
                                onChange={(obj: any) =>
                                    setOptimizationDialogState(optimizationDialogState => ({
                                        ...optimizationDialogState,
                                        startLocationId: obj.value
                                    }))
                                }
                                value={
                                    isSelectDisabled
                                        ? null
                                        : selectFromOptions.find(
                                              x =>
                                                  x.value ===
                                                  optimizationDialogState.startLocationId
                                          )
                                }
                                styles={{menuPortal: base => ({...base, zIndex: zIndex + 1})}}
                                openMenuOnFocus={true}
                                options={selectFromOptions}
                                size={SelectSizes.small}
                                componentClasses={styles.markerSelect}
                            />
                        </Flex>
                    )}
                </>
            ) : (
                <AutoPlanningContent
                    deliveryDateCol={deliveryDateCol}
                    deliveryDateValue={deliveryDateValue}
                    setDeliveryDateValue={setDeliveryDateValue}
                    showExplanation
                />
            )}
        </Flex>
    );
};
